import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, CheckCircle, AlertTriangle, MessageSquare, DollarSign, FileText, Bot, TrendingUp, Wrench, Clock, Check, Trash2, RefreshCw, Filter, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifResponse, countResponse] = await Promise.all([
        axios.get(`${API}/notifications?user_id=default&limit=100`),
        axios.get(`${API}/notifications/unread-count?user_id=default`)
      ]);
      setNotifications(notifResponse.data || []);
      setUnreadCount(countResponse.data?.count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const refreshNotifications = () => {
    setRefreshing(true);
    fetchNotifications();
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all?user_id=default`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      seller_reply: MessageSquare,
      bot_escalation: AlertTriangle,
      deal_score_change: TrendingUp,
      new_comps_found: TrendingUp,
      contract_ready: FileText,
      high_priority_deal: AlertTriangle,
      payment_received: DollarSign,
      payment_due: DollarSign,
      maintenance_update: Wrench,
      bot_status_change: Bot,
    };
    return icons[type] || Bell;
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'urgent') return 'text-red-400 bg-red-500/20';
    if (priority === 'high') return 'text-orange-400 bg-orange-500/20';
    
    const colors = {
      seller_reply: 'text-blue-400 bg-blue-500/20',
      bot_escalation: 'text-orange-400 bg-orange-500/20',
      deal_score_change: 'text-purple-400 bg-purple-500/20',
      contract_ready: 'text-green-400 bg-green-500/20',
      high_priority_deal: 'text-red-400 bg-red-500/20',
      payment_received: 'text-green-400 bg-green-500/20',
      payment_due: 'text-yellow-400 bg-yellow-500/20',
      maintenance_update: 'text-cyan-400 bg-cyan-500/20',
      bot_status_change: 'text-primary bg-primary/20',
    };
    return colors[type] || 'text-muted-foreground bg-secondary';
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: <Badge variant="destructive">Urgent</Badge>,
      high: <Badge className="bg-orange-500">High</Badge>,
      normal: <Badge variant="secondary">Normal</Badge>,
      low: <Badge variant="outline">Low</Badge>,
    };
    return badges[priority] || badges.normal;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(n => {
    if (filterType !== 'all' && n.type !== filterType) return false;
    if (filterPriority !== 'all' && n.priority !== filterPriority) return false;
    return true;
  });

  const unreadNotifications = filteredNotifications.filter(n => !n.read);
  const readNotifications = filteredNotifications.filter(n => n.read);

  const notificationTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'seller_reply', label: 'Seller Replies' },
    { value: 'bot_escalation', label: 'Bot Escalations' },
    { value: 'deal_score_change', label: 'Deal Score Changes' },
    { value: 'contract_ready', label: 'Contracts Ready' },
    { value: 'high_priority_deal', label: 'High Priority Deals' },
    { value: 'payment_received', label: 'Payments' },
    { value: 'maintenance_update', label: 'Maintenance' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="notification-center-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" data-testid="page-title">Notifications</h1>
          <p className="text-muted-foreground">Stay updated on deals, bots, and property activity</p>
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <Badge className="bg-primary text-lg px-3 py-1">{unreadCount} Unread</Badge>
          )}
          <Button variant="outline" onClick={refreshNotifications} disabled={refreshing}>
            <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button onClick={markAllAsRead}>
              <Check size={16} className="mr-2" /> Mark All Read
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Unread</p>
                <p className="text-2xl font-bold font-mono text-primary">{unreadCount}</p>
              </div>
              <Bell className="text-primary" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Escalations</p>
                <p className="text-2xl font-bold font-mono text-orange-400">
                  {notifications.filter(n => n.type === 'bot_escalation' && !n.read).length}
                </p>
              </div>
              <AlertTriangle className="text-orange-400" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Seller Replies</p>
                <p className="text-2xl font-bold font-mono text-blue-400">
                  {notifications.filter(n => n.type === 'seller_reply' && !n.read).length}
                </p>
              </div>
              <MessageSquare className="text-blue-400" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Contracts Ready</p>
                <p className="text-2xl font-bold font-mono text-green-400">
                  {notifications.filter(n => n.type === 'contract_ready' && !n.read).length}
                </p>
              </div>
              <FileText className="text-green-400" size={24} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-muted-foreground" />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              {notificationTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="unread" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="unread" className="flex items-center gap-2">
            <Bell size={16} /> Unread ({unreadNotifications.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <CheckCircle size={16} /> All ({filteredNotifications.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="unread">
          {unreadNotifications.length === 0 ? (
            <Card className="border-border">
              <CardContent className="py-16 text-center">
                <CheckCircle size={48} className="mx-auto mb-4 text-green-400" />
                <p className="text-muted-foreground">All caught up!</p>
                <p className="text-sm text-muted-foreground mt-2">No unread notifications</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {unreadNotifications.map((notification) => {
                const Icon = getNotificationIcon(notification.type);
                const colorClass = getNotificationColor(notification.type, notification.priority);
                
                return (
                  <Card 
                    key={notification.id} 
                    className="border-border border-l-4 border-l-primary cursor-pointer hover:bg-secondary/30"
                    onClick={() => markAsRead(notification.id)}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${colorClass.split(' ')[1]}`}>
                          <Icon size={20} className={colorClass.split(' ')[0]} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold">{notification.title}</span>
                            {getPriorityBadge(notification.priority)}
                          </div>
                          <p className="text-sm text-muted-foreground">{notification.message}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock size={12} /> {formatTimeAgo(notification.created_at)}
                            </span>
                            <span className="capitalize">{notification.type.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-muted-foreground">
                          <Check size={16} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="all">
          {filteredNotifications.length === 0 ? (
            <Card className="border-border">
              <CardContent className="py-16 text-center">
                <Bell size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No notifications</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notification) => {
                const Icon = getNotificationIcon(notification.type);
                const colorClass = getNotificationColor(notification.type, notification.priority);
                
                return (
                  <Card 
                    key={notification.id} 
                    className={`border-border ${!notification.read ? 'border-l-4 border-l-primary' : 'opacity-70'}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${colorClass.split(' ')[1]}`}>
                          <Icon size={20} className={colorClass.split(' ')[0]} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-semibold ${notification.read ? 'text-muted-foreground' : ''}`}>
                              {notification.title}
                            </span>
                            {getPriorityBadge(notification.priority)}
                            {notification.read && <Badge variant="outline" className="text-xs">Read</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">{notification.message}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock size={12} /> {formatTimeAgo(notification.created_at)}
                            </span>
                            <span className="capitalize">{notification.type.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                        {!notification.read && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-muted-foreground"
                            onClick={() => markAsRead(notification.id)}
                          >
                            <Check size={16} />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NotificationCenter;
